import { Injectable } from '@angular/core';
import { map, mergeMap, take } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';
import {
	AssetMappingEntityTypes,
	assetsObjectTypes,
	ICmmsAsset,
	ICmmsAssetCreateVariables,
	ICmmsAssetEditPayload,
	ICmmsAssetEntityToAttach,
	ICmmsAssetPaginatedData,
	ICmmsAssetQueryVariables,
	ILinkAssetsObjectsVariables,
	IUnlinkAssetObjects,
} from '@app/pages/cmms/interfaces/assets.interfaces';
import {
	CreateAssetMutation,
	DeleteAssetMutation,
	DeleteAssetsMutation,
	EditAsset,
	LinkAssetsObjects,
	UnlinkAssetObjects,
} from '@app/pages/cmms/graphql/assets.mutations';
import {
	AssetsClassificationAttachedFileQuery,
	AssetsQuery,
	SearchAssetsQuery,
} from '@app/pages/cmms/graphql/assets.queries';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';
import { AuErrorAdapter, AuUtilsFunctions } from '@app/shared';

import { IRequest } from '@app/pages/cmms/interfaces/request.interfaces';
import { IIncident } from '@app/pages/cmms/interfaces/incidents.interface';
import { Store } from '@ngrx/store';
import { CmmsPages, ICmmsState } from '@app/pages/cmms/state/cmms.reducer';
import {
	setCmmsAssetForEditing,
	storeCmmsAssetsQueryVariables,
	updateCmmsAssetSuccess,
} from '@app/pages/cmms/components/assets/state/assets.actions';
import { IAttachedFile } from '@app/pages/cmms/interfaces/files.interfaces';
import { ACTIVE_PAGE_CONFIG } from '@app/pages/cmms/configs/active-page.config';
import { AuRightSidebarService } from '@core/services/au-right-sidebar.service';
import { IAuPaginationData } from '@shared/ui-components/au-paginator/pagination-page.info';
import { iconEnum } from '@app/configs/model/IconEnum';
import { NewAssetComponent } from '@app/pages/cmms/components/assets/new-asset/new-asset.component';
import { TranslateService } from '@tolgee/ngx';
import { IWorkOrder } from '@app/domain/cmms-types/workorder-types';

@Injectable({ providedIn: 'root' })
export class AssetsService {
	constructor(
		private apollo: Apollo,
		private notify: IconSnackBarService,
		private store$: Store<ICmmsState>,
		private sidebarService: AuRightSidebarService,
		private translateService: TranslateService
	) {}

	loadCmmsAssets(variables: ICmmsAssetQueryVariables = { first: 10 }) {
		// Due to filter and sort service are provided in component,
		// we need to store the query variables in store to use that in effects
		// when we update table after creating new item
		this.store$.dispatch(storeCmmsAssetsQueryVariables({ variables }));
		return this.apollo
			.query<ICmmsAssetPaginatedData>({
				query: AssetsQuery,
				fetchPolicy: 'network-only',
				variables,
			})
			.pipe(map(response => this.getAssetsData(structuredClone(response.data.assets))));
	}

	loadAssetClassificationAttachedFiles(
		pkIn: string,
		paginationInfo: IAuPaginationData,
		variables?: { search?: string; type?: string[]; classificationsAsset?: string[] }
	) {
		return this.apollo
			.query<{ assets: ICmmsAsset }>({
				query: AssetsClassificationAttachedFileQuery,
				fetchPolicy: 'network-only',
				variables: {
					pkIn,
					...paginationInfo,
					...variables,
				},
			})
			.pipe(
				take(1),
				map((value: any) => value.data.assets),
				map(assets => {
					return {
						classificationAttachedFileEntities:
							assets.edges[0]?.node.classificationAttachedFileEntities.edges.map(file => {
								return file.node;
							}),
						pageInfo: assets.edges[0]?.node.classificationAttachedFileEntities.pageInfo,
						totalCount:
							assets.edges[0]?.node.classificationAttachedFileEntities.totalCount,
					};
				})
			);
	}

	getAssetsData(paginatedAssets) {
		const assets = paginatedAssets.edges.map(edge => {
			if (edge.node.classificationAttachedFileEntities) {
				edge.node.classificationAttachedFileEntities =
					edge.node.classificationAttachedFileEntities.edges.map(
						(edge: any) => edge.node
					);
			}

			return edge.node;
		});

		return {
			assets,
			assetPagination: {
				pageInfo: paginatedAssets.pageInfo,
				totalCount: paginatedAssets.totalCount,
			},
		};
	}

	createAsset(assets: Partial<ICmmsAssetCreateVariables>[]) {
		return this.apollo
			.mutate<ICmmsAsset[]>({
				mutation: CreateAssetMutation,
				fetchPolicy: 'network-only',
				variables: {
					assets,
				},
			})
			.pipe(
				map((value: any) => {
					const payload = structuredClone(value);
					payload.data.createAsset.assets.map(asset => {
						if (asset.classificationAttachedFileEntities) {
							asset.classificationAttachedFileEntities =
								asset.classificationAttachedFileEntities.edges.map(file => {
									return file.node;
								});
						}
					});
					return payload.data.createAsset.assets as ICmmsAsset[];
				})
			);
	}

	/**
	 * Create asset with attach data
	 */
	createAssetWithAttach(
		asset: Partial<ICmmsAssetCreateVariables>,
		entityToAttach?: ICmmsAssetEntityToAttach
	) {
		return this.createAsset([asset]).pipe(
			mergeMap(assets => {
				return this.linkAssetsObjectsVariables(
					this.formatDataForLinkOperations(assets[0], entityToAttach)
				).pipe(
					map(data => {
						data['asset'] = assets[0];
						return data;
					})
				);
			})
		);
	}

	editAsset(asset: ICmmsAssetEditPayload) {
		return this.apollo
			.mutate({
				mutation: EditAsset,
				variables: { asset },
			})
			.pipe(take(1));
	}

	deleteAsset(pk: string) {
		return this.apollo.mutate({
			mutation: DeleteAssetMutation,
			fetchPolicy: 'network-only',
			variables: { pk },
		});
	}

	deleteAssets(pks: string[]) {
		return this.apollo.mutate({
			mutation: DeleteAssetsMutation,
			fetchPolicy: 'network-only',
			variables: { assets: pks },
		});
	}

	formatDataForLinkOperations(
		asset: ICmmsAsset,
		entityToAttach?: ICmmsAssetEntityToAttach
	): ILinkAssetsObjectsVariables {
		const mapEntities = (entities?: any[]) =>
			entities ? entities.map(entity => entity.pk) : [];

		return {
			assets: [asset.pk],
			files: mapEntities(entityToAttach?.files),
			workOrders: mapEntities(entityToAttach?.workOrders),
			requests: mapEntities(entityToAttach?.requests),
			incidents: mapEntities(entityToAttach?.incident),
			classifications: mapEntities(entityToAttach?.classifications),
		};
	}

	afterMappingAssetSuccess(
		currentAsset: ICmmsAsset,
		type: AssetMappingEntityTypes,
		newLinkedEntity: IWorkOrder[] | IRequest[] | IIncident[] | IAttachedFile[],
		skipTableReload = false
	) {
		// TODO this function should be refactored and move in assetEffect
		// TODO In the future probably API be refactored in the same way as in the update for file/WO/Request/Incident
		// Work only for one asset, when in asset array use same asset pk,
		// and only for one of update entity type file/WO/Request/Incident

		this.notify.success(
			`${this.translateService.instant('asset')} ${this.translateService.instant('has_been_updated_successfully')}`
		);
		const updatedAsset = AuUtilsFunctions.deepCopyObject(currentAsset);

		if (type === AssetMappingEntityTypes.WorkOrder) {
			updatedAsset.workOrders = newLinkedEntity as IWorkOrder[];
		} else if (type === AssetMappingEntityTypes.Incident) {
			updatedAsset.incidents = newLinkedEntity as IIncident[];
		} else if (type === AssetMappingEntityTypes.Request) {
			updatedAsset.requests = newLinkedEntity as IRequest[];
		} else if (type === AssetMappingEntityTypes.FileEntity) {
			updatedAsset.assetAttachedFiles = newLinkedEntity as IAttachedFile[];
		}

		this.store$.dispatch(
			updateCmmsAssetSuccess({ asset: updatedAsset, skipTableReload })
		);
	}

	linkAssetsObjectsVariables(
		variables: ILinkAssetsObjectsVariables,
		unlink?: IUnlinkAssetObjects[]
	) {
		unlink?.map(remove => {
			this.unlinkAssetsObjects(remove).subscribe({
				error: error => {
					this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
				},
			});
		});

		return this.apollo.mutate({
			mutation: LinkAssetsObjects,
			variables,
		});
	}

	unlinkAssetsObjects(variables: IUnlinkAssetObjects) {
		return this.apollo.mutate({ mutation: UnlinkAssetObjects, variables });
	}

	getAssetObjectTypeByMappingType(type: AssetMappingEntityTypes): assetsObjectTypes {
		if (type === AssetMappingEntityTypes.Incident) {
			return assetsObjectTypes.incidents;
		} else if (type === AssetMappingEntityTypes.WorkOrder) {
			return assetsObjectTypes.workOrders;
		} else if (type === AssetMappingEntityTypes.Request) {
			return assetsObjectTypes.requests;
		} else if (type === AssetMappingEntityTypes.FileEntity) {
			return assetsObjectTypes.files;
		} else {
			throw new Error(`${type} is not compatible assetsObjectTypes.`);
		}
	}

	getAssetByPk(pkIn: string) {
		return this.apollo
			.query({
				query: SearchAssetsQuery,
				fetchPolicy: 'network-only',
				variables: {
					pkIn,
				},
			})
			.pipe(map((value: any) => value.data.assets.edges.map(data => data.node)[0]));
	}

	getAssetByGlobalId(globalId: string) {
		return this.apollo
			.query({
				query: SearchAssetsQuery,
				fetchPolicy: 'network-only',
				variables: {
					globalId,
				},
			})
			.pipe(map((value: any) => value.data.assets?.edges.map(data => data.node)[0]));
	}

	viewAssetInWorkspace(asset: ICmmsAsset): void {
		this.store$.dispatch(setCmmsAssetForEditing({ assetEdit: asset }));
		this.sidebarService.setComponentAndOpen(
			ACTIVE_PAGE_CONFIG[CmmsPages.editAsset].createComponent,
			{
				...ACTIVE_PAGE_CONFIG[CmmsPages.editAsset].sidebarConfig,
				title: { text: 'asset_name', endfix: asset.name },
			}
		);
	}

	openNewAssetWorkspace() {
		this.sidebarService.setComponentAndOpen(NewAssetComponent, {
			title: { prefix: 'new_text', text: 'asset' },
			icon: iconEnum.asset,
			actionBtnName: 'submit',
			showContentActions: true,
			propagateCloseEvent: true,
		});
	}
}
